import type {
	FailedPendingResponse,
	HTTPValidationError,
	PricingRuleApplyErrorResponse,
	PricingRuleApplyModel,
	PricingRuleCreateModel,
	PricingRuleDraftSaveModel,
	PricingRuleImportForm,
	PricingRuleModel,
	PricingRulePublishModel,
	PricingRuleUpdateModel,
	PricingRuleVersionExportParams,
	PricingRuleVersionModel,
	PricingRuleVersionUpdateModel,
} from '@/shared/generated'
import type { UseMutationOptions } from '@tanstack/react-query'
import type { AxiosError } from 'axios'

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { client } from '@/shared/api/client'

import { pricingRuleQueries } from './queries'

export function useCreatePricingRuleMutation(
	options?: UseMutationOptions<
		PricingRuleModel,
		AxiosError,
		PricingRuleCreateModel
	>
) {
	const queryClient = useQueryClient()

	return useMutation<PricingRuleModel, AxiosError, PricingRuleCreateModel>({
		mutationFn: (body) => client.pricingRule.pricingRuleCreate(body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['pricing-rules'] })
		},
		...(options || {}),
	})
}

export function useUpdatePricingRuleMutation(
	options?: UseMutationOptions<
		PricingRuleModel,
		AxiosError,
		{ id: string; data: PricingRuleUpdateModel }
	>
) {
	const queryClient = useQueryClient()

	return useMutation<
		PricingRuleModel,
		AxiosError,
		{ id: string; data: PricingRuleUpdateModel }
	>({
		mutationFn: ({ id, data }) =>
			client.pricingRule.pricingRuleUpdate({ pricingRuleId: id }, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['pricing-rules'] })
		},
		...(options || {}),
	})
}

export function useDeletePricingRuleMutation(
	options?: UseMutationOptions<void, AxiosError, string>
) {
	const queryClient = useQueryClient()

	return useMutation<void, AxiosError, string>({
		mutationFn: (id) =>
			client.pricingRule.pricingRuleDelete({ pricingRuleId: id }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['pricing-rules'] })
		},
		...(options || {}),
	})
}

export function useDuplicatePricingRuleMutation(
	options?: UseMutationOptions<PricingRuleModel, AxiosError, string>
) {
	const queryClient = useQueryClient()

	return useMutation<PricingRuleModel, AxiosError, string>({
		mutationFn: (id) =>
			client.pricingRule.pricingRuleDuplicate({ pricingRuleId: id }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['pricing-rules'] })
		},
		...(options || {}),
	})
}

export function useApplyPricingRuleMutation(
	options?: UseMutationOptions<
		void,
		AxiosError<
			| PricingRuleApplyErrorResponse
			| HTTPValidationError
			| FailedPendingResponse
		>,
		{ id: string; data: PricingRuleApplyModel }
	>
) {
	return useMutation<
		void,
		AxiosError<
			| PricingRuleApplyErrorResponse
			| HTTPValidationError
			| FailedPendingResponse
		>,
		{ id: string; data: PricingRuleApplyModel }
	>({
		mutationFn: ({ id, data }) =>
			client.pricingRule.pricingRuleApply({ pricingRuleId: id }, data),
		...(options || {}),
	})
}

export function useSaveDraftMutation(
	options?: UseMutationOptions<
		PricingRuleVersionModel,
		AxiosError,
		{ id: string; data: PricingRuleDraftSaveModel }
	>
) {
	const queryClient = useQueryClient()

	return useMutation<
		PricingRuleVersionModel,
		AxiosError,
		{ id: string; data: PricingRuleDraftSaveModel }
	>({
		mutationFn: ({ id, data }) =>
			client.pricingRule.pricingRuleDraftSave({ pricingRuleId: id }, data),
		onSuccess: (res, { id }) => {
			// The save response is a full PricingRuleVersionModel — same shape the
			// draft query caches — so any other reader (e.g. re-entering the editor)
			// sees the latest hash instead of the pre-save one. See F9a.
			queryClient.setQueryData(pricingRuleQueries.draft(id).queryKey, res)
		},
		...(options || {}),
	})
}

export function usePublishPricingRuleMutation(
	options?: UseMutationOptions<
		PricingRuleVersionModel,
		AxiosError,
		{ id: string; data: PricingRulePublishModel }
	>
) {
	const queryClient = useQueryClient()

	return useMutation<
		PricingRuleVersionModel,
		AxiosError,
		{ id: string; data: PricingRulePublishModel }
	>({
		mutationFn: ({ id, data }) =>
			client.pricingRule.pricingRulePublish({ pricingRuleId: id }, data),
		onSuccess: (_d, { id }) => {
			queryClient.invalidateQueries({
				queryKey: ['pricing-rules', 'versions', id],
			})
		},
		...(options || {}),
	})
}

export function useUpdateVersionMutation(
	options?: UseMutationOptions<
		PricingRuleVersionModel,
		AxiosError,
		{ id: string; vid: string; data: PricingRuleVersionUpdateModel }
	>
) {
	const queryClient = useQueryClient()

	return useMutation<
		PricingRuleVersionModel,
		AxiosError,
		{ id: string; vid: string; data: PricingRuleVersionUpdateModel }
	>({
		mutationFn: ({ id, vid, data }) =>
			client.pricingRule.pricingRuleVersionUpdate(
				{ pricingRuleId: id, pricingRuleVersionId: vid },
				data
			),
		onSuccess: (_d, { id }) => {
			queryClient.invalidateQueries({
				queryKey: ['pricing-rules', 'versions', id],
			})
		},
		...(options || {}),
	})
}

export function useRestoreVersionMutation(
	options?: UseMutationOptions<
		PricingRuleVersionModel,
		AxiosError,
		{ id: string; vid: string }
	>
) {
	const queryClient = useQueryClient()

	return useMutation<
		PricingRuleVersionModel,
		AxiosError,
		{ id: string; vid: string }
	>({
		mutationFn: ({ id, vid }) =>
			client.pricingRule.pricingRuleVersionRestore({
				pricingRuleId: id,
				pricingRuleVersionId: vid,
			}),
		onSuccess: (_d, { id }) => {
			queryClient.invalidateQueries({
				queryKey: ['pricing-rules', 'draft', id],
			})
		},
		...(options || {}),
	})
}

export function useImportPricingRuleMutation(
	pricingRuleId: string,
	options?: UseMutationOptions<
		PricingRuleVersionModel,
		AxiosError,
		PricingRuleImportForm
	>
) {
	return useMutation<
		PricingRuleVersionModel,
		AxiosError,
		PricingRuleImportForm
	>({
		mutationFn: (body) =>
			client.pricingRule.pricingRuleDraftImport({ pricingRuleId }, body),
		...(options || {}),
	})
}

export function useExportPricingRuleDraftMutation(
	pricingRuleId: string,
	options?: UseMutationOptions<any, AxiosError>
) {
	return useMutation<any, AxiosError>({
		mutationFn: () =>
			client.pricingRule.pricingRuleDraftExport({ pricingRuleId }),
		...(options || {}),
	})
}

export function useExportPricingRuleVersionMutation(
	options?: UseMutationOptions<any, AxiosError, PricingRuleVersionExportParams>
) {
	return useMutation<any, AxiosError, PricingRuleVersionExportParams>({
		mutationFn: (params) => client.pricingRule.pricingRuleVersionExport(params),
		...(options || {}),
	})
}
